import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ProForm, ProFormText, ProFormSelect, ModalForm } from '@ant-design/pro-components';

interface User {
  id: number;
  account: string;
  username: string;
  company_name: string;
  phone: string;
  email: string;
  type: string;
  status_flag: number;
  create_time: string;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = ProForm.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setUsers(resp.data || []);
      } else {
        message.error(resp?.error || '获取用户列表失败');
      }
    } catch (err) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchUsers();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingUser ? `/api/admin/users/${editingUser.id}` : '/api/admin/users';
      const method = editingUser ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingUser ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchUsers();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 过滤数据
  const filteredUsers = users.filter(user => 
    user.account?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    user.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    user.phone?.includes(searchKeyword)
  );

  // 获取类型标签颜色
  const getTypeColor = (type: string) => {
    switch (type) {
      case '正式用户': return 'success';
      case '预注册': return 'warning';
      case '审核中': return 'processing';
      case '已冻结': return 'error';
      default: return 'default';
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '账号',
      dataIndex: 'account',
      key: 'account',
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '姓名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)} style={{ borderRadius: 12 }}>
          {type || '未知'}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status_flag',
      key: 'status_flag',
      render: (flag: number) => (
        <Tag color={flag === 1 ? 'success' : 'error'} style={{ borderRadius: 12 }}>
          {flag === 1 ? '正常' : '已删除'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个用户吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <UserOutlined />
          </div>
          <div>
            <h2>用户管理</h2>
            <p>管理系统中的所有用户账号</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索用户..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 240, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchUsers}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增用户
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredUsers.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            style: { marginTop: 16 }
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingUser ? '编辑用户' : '新增用户'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="account"
            label="账号"
            placeholder="请输入账号"
            rules={[{ required: true, message: '请输入账号' }]}
          />
          <ProFormText.Password
            name="password"
            label="密码"
            placeholder={editingUser ? "不修改请留空" : "请输入密码"}
            rules={editingUser ? [] : [{ required: true, message: '请输入密码' }]}
          />
          <ProFormText
            name="username"
            label="姓名"
            placeholder="请输入姓名"
          />
          <ProFormText
            name="company_name"
            label="公司名称"
            placeholder="请输入公司名称"
          />
          <ProFormText
            name="phone"
            label="手机号"
            placeholder="请输入手机号"
          />
          <ProFormText
            name="email"
            label="邮箱"
            placeholder="请输入邮箱"
          />
          <ProFormSelect
            name="type"
            label="类型"
            placeholder="请选择类型"
            options={[
              { label: '预注册', value: '预注册' },
              { label: '审核中', value: '审核中' },
              { label: '正式用户', value: '正式用户' },
              { label: '已冻结', value: '已冻结' },
            ]}
          />
        </div>
      </ModalForm>
    </div>
  );
}
